import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";

// A response belongs to one account, customer and language. Never render the
// previous customer's wishlist or AI-automation flag while the next one loads,
// even when the two requests above race and resolve out of order.
export default function useCockpitRequests({ lang, clientId, userId, accountClientId }) {
  const scopeKey = JSON.stringify([userId, clientId || accountClientId || null, lang]);
  const currentScope = useRef(scopeKey);
  currentScope.current = scopeKey;
  const requestId = useRef(0);
  const mounted = useRef(false);
  const [state, setState] = useState(null);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; requestId.current += 1; };
  }, []);
  const load = useCallback(async () => {
    if (!mounted.current || currentScope.current !== scopeKey) return;
    const id = ++requestId.current;
    const isCurrent = () => mounted.current && currentScope.current === scopeKey && requestId.current === id;
    setState({ scopeKey, data: null, error: false });
    const qs = clientId ? `?client_id=${encodeURIComponent(clientId)}` : "";
    try {
      const [{ data: reqData }, config] = await Promise.all([
        api.get(`/cockpit/ai/requests${qs}`),
        api.get(`/cockpit/ai/requests/config${qs}`).catch(() => ({ data: { automation: null } })),
      ]);
      const expectedClient = clientId || accountClientId;
      if (!reqData || !Array.isArray(reqData.requests) || reqData.requests.some(r =>
        !r || typeof r.id !== "string" || !r.id || (expectedClient && r.client_id && r.client_id !== expectedClient))) {
        throw new Error("Invalid request response");
      }
      const generationEnabled = config.data?.automation?.generation_enabled === true;
      if (isCurrent()) setState({ scopeKey, data: { requests: reqData.requests, generationEnabled }, error: false });
    } catch {
      if (isCurrent()) setState({ scopeKey, data: null, error: true });
    }
  }, [scopeKey, clientId, accountClientId]);
  useEffect(() => { load(); }, [load]);
  const current = state?.scopeKey === scopeKey ? state : null;
  return { data: current?.data || null, loadError: !!current?.error, load };
}
